"use client"

import { logger } from "@src/utils/logger"
import { useEffect } from "react"
import "../styles/global.css"
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid"
import Button from "@src/components/Button"
import { NearIcon } from "@src/icons"
import { ThemeProvider } from "next-themes"
import Link from "next/link"

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
}) {
  useEffect(() => {
    logger.error(new Error("Global error", { cause: error }))
  }, [error])

  return (
    <html
      lang="en"
      suppressHydrationWarning
      className="bg-white text-gray-900 antialiased selection:bg-green-500 selection:text-black dark:bg-gray-850 dark:text-white"
    >
      <title>near.com | Error</title>
      <body>
        <ThemeProvider attribute="class">
          <main className="flex min-h-screen flex-col items-center justify-center bg-white dark:bg-gray-850">
            <div className="p-5 md:py-10">
              <Link href="/" className="flex items-center gap-2.5">
                <NearIcon className="size-12 shrink-0 text-black" />
                <span className="sr-only">Home</span>
              </Link>
            </div>

            <div
              className="flex grow flex-col items-center justify-center p-5 md:py-10 md:pb-42"
              role="alert"
              aria-live="assertive"
            >
              <div className="flex size-10 items-center justify-center rounded-xl bg-gray-900 text-green-500 dark:bg-green-500/10">
                <ExclamationTriangleIcon className="size-6 shrink-0" />
              </div>
              <h1 className="mt-4 text-pretty text-center font-semibold text-3xl/[1.1] text-gray-900 tracking-tight sm:mt-6 sm:text-4xl/[1.1] lg:text-5xl/[1.1] dark:text-white">
                Oops! Something went wrong...
              </h1>

              {error.digest && (
                <code className="mt-6 rounded-xl bg-gray-100 px-3 py-1 text-center font-mono font-semibold text-base text-gray-700 tracking-tight sm:mt-8 sm:text-lg dark:bg-gray-800 dark:text-white">
                  Error ID: {error.digest}
                </code>
              )}

              <Button
                size="xl"
                onClick={() => window.location.reload()}
                className="mt-12 sm:mt-16"
              >
                <ArrowPathIcon className="size-5 shrink-0" />
                Refresh page
              </Button>
            </div>
          </main>
        </ThemeProvider>
      </body>
    </html>
  )
}
